import { useStore } from '@data/storages';
import i18n from 'i18next';
import { toast } from 'sonner';
import {
  InfiniteData,
  QueryClient,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
  UseQueryOptions,
} from '@tanstack/react-query';

import * as api from '../api';
import * as queryKeys from '../constants/keys';
import { pmSprintProductsQueryKey } from './pm-sprint-products-query-key';
import { invalidatePmProductLists } from './pm-product-lists';
import {
  ApprovalRecipients,
  ApproveSprintFormValues,
  ClientProductEntity,
  DisputeSprintFormValues,
  ProductEntity,
  IError,
  ListResponse,
  MembershipEntity,
  PmParams,
  PmProductsInfiniteData,
  RevokeDisputeValues,
  SendSprintFormValues,
  SprintProductEntity,
  SprintProductClientEntity,
  SprintProductStatus,
  SprintReportMailDraft,
  MailBlock,
  UnclosedSprintEntity,
} from '../utils/types';

const updateProductPages = (products: InfiniteData<PmProductsInfiniteData>, values: Partial<SprintProductEntity>) =>
  products.pages.map(page =>
    page.sprintId === values.sprintId
      ? {
          ...page,
          items: page.items.map(item =>
            item.id === values.productId
              ? {
                  ...item,
                  sprintProducts: item.sprintProducts?.length ? [{ ...item.sprintProducts[0], ...values }] : [values],
                }
              : item,
          ),
        }
      : page,
  );

const handleListMutation = (
  queryClient: QueryClient,
  organizationId,
  params: PmParams,
  values: Partial<SprintProductEntity>,
): void => {
  queryClient.setQueryData(
    [queryKeys.PM_PRODUCTS, { organizationId, params }],
    (products: InfiniteData<PmProductsInfiniteData> | undefined) => {
      // The screen doing the closing may not hold this list at all — the products reporting list reads
      // one entry per sprint instead. Writing into nothing used to throw out of onMutate, which took
      // the whole mutation with it.
      if (!products) return products;
      return { ...products, pages: updateProductPages(products, values) };
    },
  );
};

const handleDetailMutation = (
  queryClient: QueryClient,
  detail: { sprintId: string; productId: string },
  newSprintProduct: Partial<SprintProductEntity>,
) => {
  const key = [queryKeys.PM_SPRINT_PRODUCTS, detail];
  const cacheData = queryClient.getQueryData(key) as ListResponse<SprintProductEntity>;
  const newObj = Object.assign({}, ...(cacheData?.items ?? []), newSprintProduct);

  queryClient.setQueryData(key, () => ({
    count: 1,
    items: [newObj],
  }));
};

const handleListSuccess = (
  queryClient: QueryClient,
  organizationId,
  params: PmParams,
  values: Partial<SprintProductEntity>,
  data: SprintProductEntity,
) => {
  queryClient.setQueryData(
    [queryKeys.PM_PRODUCTS, { organizationId, params }],
    (products: InfiniteData<PmProductsInfiniteData> | undefined) => {
      if (!products) return products;
      return { ...products, pages: updateProductPages(products, { ...values, ...data }) };
    },
  );
};

/**
 * The products reporting list holds one cache entry per sprint (see useSprintPages), so a status
 * change has to reach the entry for the sprint it happened in — the row otherwise keeps its old
 * status until something else refetches it.
 */
const patchSprintPage = (
  queryClient: QueryClient,
  values: Partial<SprintProductEntity>,
  patch: (product: ProductEntity) => ProductEntity,
): void => {
  queryClient.setQueriesData<ProductEntity[]>(
    {
      predicate: query =>
        query.queryKey[0] === queryKeys.PM_PRODUCTS_BY_SPRINT &&
        (query.queryKey[1] as { sprintId?: string })?.sprintId === values.sprintId,
    },
    products => products?.map(product => (product.id === values.productId ? patch(product) : product)),
  );
};

const applyToSprintPages = (queryClient: QueryClient, values: Partial<SprintProductEntity>): void =>
  patchSprintPage(queryClient, values, product => ({
    ...product,
    sprintProducts: (product.sprintProducts?.length
      ? [{ ...product.sprintProducts[0], ...values }]
      : [values]) as SprintProductEntity[],
  }));

/** Settles one sprint's entry with the server, rather than refetching every sprint on screen. */
const invalidateSprintPage = (queryClient: QueryClient, values: Partial<SprintProductEntity>): void => {
  queryClient.invalidateQueries({
    predicate: query =>
      query.queryKey[0] === queryKeys.PM_PRODUCTS_BY_SPRINT &&
      (query.queryKey[1] as { sprintId?: string })?.sprintId === values.sprintId,
  });
};

export const useCreateSprintProduct = (
  organizationId: string,
  params: PmParams,
  detail?: { sprintId: string; productId: string },
): UseMutationResult<SprintProductEntity, IError, Partial<SprintProductEntity>, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: Partial<SprintProductEntity>) => api.createSprintProduct(organizationId, values),
    onMutate: async (values: Partial<SprintProductEntity>) => {
      const previousPmProducts = queryClient.getQueryData([
        queryKeys.PM_PRODUCTS,
        { organizationId, params },
      ]) as InfiniteData<PmProductsInfiniteData>;

      const previousSprintProducts = queryClient.getQueryData(
        pmSprintProductsQueryKey(detail),
      ) as ListResponse<SprintProductEntity>;

      if (!detail) handleListMutation(queryClient, organizationId, params, values);
      applyToSprintPages(queryClient, values);

      return { previousPmProducts, previousSprintProducts };
    },
    onSuccess: (data, values) => {
      if (detail) {
        queryClient.invalidateQueries({ queryKey: [queryKeys.PM_SPRINT_PRODUCTS, detail] });
      } else {
        handleListSuccess(queryClient, organizationId, params, values, data);
      }
      invalidateSprintPage(queryClient, values);
      // Giving a sprint its first report is how it stops being one a PM owes a close (or, opened rather
      // than closed, how it becomes one) — either way the Insights card is out of date.
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_UNCLOSED_SPRINTS] });
    },

    onError: (_, values, context) => {
      queryClient.setQueryData([queryKeys.PM_PRODUCTS, { organizationId, params }], context?.previousPmProducts);
      queryClient.setQueryData(pmSprintProductsQueryKey(detail), context?.previousSprintProducts);
      // The optimistic status went into this sprint's own entry too. Refetching it is the rollback:
      // a refused close otherwise leaves the row claiming a status the server never accepted.
      invalidateSprintPage(queryClient, values);
    },
  });
};

export const useUpdateSprintProduct = (
  organizationId: string,
  params: PmParams,
  detail?: { sprintId: string; productId: string },
): UseMutationResult<SprintProductEntity, IError, Partial<SprintProductEntity>, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: Partial<SprintProductEntity>) => api.updateSprintProduct(values),
    onMutate: async (values: Partial<SprintProductEntity>) => {
      const previousPmProducts = queryClient.getQueryData([
        queryKeys.PM_PRODUCTS,
        { organizationId, params },
      ]) as InfiniteData<PmProductsInfiniteData>;

      const previousSprintProducts = queryClient.getQueryData(
        pmSprintProductsQueryKey(detail),
      ) as ListResponse<SprintProductEntity>;

      if (detail) {
        handleDetailMutation(queryClient, detail, values);
      } else {
        handleListMutation(queryClient, organizationId, params, values);
      }
      applyToSprintPages(queryClient, values);
      return { previousPmProducts, previousSprintProducts };
    },
    onSuccess: (data, values) => {
      if (!detail) handleListSuccess(queryClient, organizationId, params, values, data);
      invalidateSprintPage(queryClient, values);
      // Closing drops the sprint off the "needs closing" card; reopening puts it back.
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_UNCLOSED_SPRINTS] });
      // A status change is no longer all a close does: closing a report we changed answers the remark on
      // it, which settles it and takes it out of everyone's queue. The optimistic patch only knows the
      // status it asked for, so the report itself has to come back from the server.
      if (detail) queryClient.invalidateQueries({ queryKey: [queryKeys.PM_SPRINT_PRODUCTS, detail] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_UNREAD_COMMENTS] });
    },

    onError: (_, values, context) => {
      queryClient.setQueryData([queryKeys.PM_PRODUCTS, { organizationId, params }], context?.previousPmProducts);
      queryClient.setQueryData(pmSprintProductsQueryKey(detail), context?.previousSprintProducts);
      // The optimistic status went into this sprint's own entry too. Refetching it is the rollback:
      // a refused close otherwise leaves the row claiming a status the server never accepted.
      invalidateSprintPage(queryClient, values);
    },
  });
};

export const useApproveSprintProduct = (): UseMutationResult<
  ClientProductEntity,
  IError,
  ApproveSprintFormValues,
  unknown
> => {
  const queryClient = useQueryClient();
  const { tokenData } = useStore();
  return useMutation({
    mutationFn: (values: ApproveSprintFormValues) => api.approveSprintProduct(values),
    onMutate: (values: ApproveSprintFormValues) => {
      const detail = { sprintId: values.sprintId, productId: values.productId };

      const previousDetail = queryClient.getQueryData([
        queryKeys.PM_SPRINT_PRODUCTS,
        detail,
      ]) as ListResponse<ClientProductEntity>;

      // The cache holds the report, not the product this key is typed for, and the remark lives on the report.
      const previous = previousDetail?.items?.[0] as unknown as SprintProductEntity | undefined;
      // Whether this approval is the answer to a remark that was still standing, which is what the server
      // decides too. A remark already dismissed keeps the record of who dismissed it.
      const settlesARemark = !!previous?.disputedAt && !!previous?.comment && !previous?.disputeResolvedAt;

      handleDetailMutation(queryClient, detail, {
        ...previous,
        id: values.sprintProductId,
        status: SprintProductStatus.approved,
        approvedBy: { email: tokenData?.email ?? '' },
        approvedAt: new Date(),
        // The remark stays on the report; what goes is the notice about our answer to it, since the client
        // has now answered back, and the dot it was wearing.
        disputeResolution: undefined,
        ...(settlesARemark && { disputeResolvedAt: new Date(), disputeResolvedBy: { email: tokenData?.email ?? '' } }),
        viewerHasUnreadComment: false,
        approvalRecipients: values.approvalRecipients,
      });

      return { previousDetail };
    },
    onSuccess: () => {
      toast.success(i18n.t('reportApproved'));
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENT_SPRINT_PRODUCTS] });
      // An approval removes the sprint from the "awaiting approval" list on Insights.
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_SPRINT_PRODUCTS_LIST] });
      // ...and moves it on from "closed" wherever a PM is reading the sprint's status, which is what the
      // product's Sprints tab offers the approval from.
      invalidatePmProductLists(queryClient);
      // The report itself, which the optimistic patch above can only half-fill: it knows the approver's
      // email from the token but not their name. Without this the report keeps showing the address.
      queryClient.invalidateQueries({ queryKey: pmSprintProductsQueryKey() });
      // An approval settles any remark still standing, so it leaves the unread queue the nav badge counts.
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_UNREAD_COMMENTS] });
    },
    onError: (_, values, context) => {
      const detail = { sprintId: values.sprintId, productId: values.productId };
      queryClient.setQueryData([queryKeys.PM_SPRINT_PRODUCTS, detail], context?.previousDetail);
    },
  });
};

export const useDisputeSprintProduct = (): UseMutationResult<
  ClientProductEntity,
  IError,
  DisputeSprintFormValues,
  unknown
> => {
  const queryClient = useQueryClient();
  const { tokenData } = useStore();
  return useMutation({
    mutationFn: (values: DisputeSprintFormValues) => api.disputeSprintProduct(values),
    onMutate: (values: DisputeSprintFormValues) => {
      const detail = { sprintId: values.sprintId, productId: values.productId };

      const previousDetail = queryClient.getQueryData([
        queryKeys.PM_SPRINT_PRODUCTS,
        detail,
      ]) as ListResponse<ClientProductEntity>;

      handleDetailMutation(queryClient, detail, {
        // Optional: a dispute recorded from a list has never opened the report, so this cache can be cold.
        ...previousDetail?.items?.[0],
        id: values.sprintProductId,
        // A disputed report waits on us again, which is what closed means.
        status: SprintProductStatus.closed,
        disputedBy: { email: tokenData?.email ?? '' },
        disputedAt: new Date(),
        comment: values.remark,
        disputeRecipients: values.disputeRecipients,
        // A fresh remark answers whatever we told them about the last one, so that answer goes with it —
        // the same three fields clearDisputeAnswer drops on the server.
        disputeResolution: undefined,
        disputeResolvedAt: undefined,
        disputeResolvedBy: undefined,
        // Whoever is writing has read their own words, so no dot for them. Their colleagues get one on
        // the next fetch: the receipt this leaves behind is the writer's alone.
        viewerHasUnreadComment: false,
      });

      return { previousDetail };
    },
    onSuccess: () => {
      toast.success(i18n.t('reportDisputed'));
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENT_SPRINT_PRODUCTS] });
      // A dispute puts the report back in front of us, so every list that counts what is waiting on
      // whom is out of date: Insights, the product lists, and the report itself.
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_SPRINT_PRODUCTS_LIST] });
      invalidatePmProductLists(queryClient);
      queryClient.invalidateQueries({ queryKey: pmSprintProductsQueryKey() });
      // The remark arrives as an unread comment, which is what the badge on the nav counts.
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_UNREAD_COMMENTS] });
    },
    onError: (_, values, context) => {
      const detail = { sprintId: values.sprintId, productId: values.productId };
      queryClient.setQueryData([queryKeys.PM_SPRINT_PRODUCTS, detail], context?.previousDetail);
    },
  });
};

/**
 * Dismissing a remark: it is settled, the report goes back to waiting on the client, and their side is
 * told we looked and stand by it. Every list that counts what is waiting on whom has to recount, and the
 * remark leaves the unread queue with it.
 */
export const useDismissDispute = (): UseMutationResult<SprintProductEntity, IError, RevokeDisputeValues, unknown> => {
  const queryClient = useQueryClient();
  const { tokenData } = useStore();
  return useMutation({
    mutationFn: (values: RevokeDisputeValues) => api.dismissDispute(values.sprintProductId, values.note),
    onMutate: (values: RevokeDisputeValues) => {
      const detail = { sprintId: values.sprintId, productId: values.productId };
      const previousDetail = queryClient.getQueryData([
        queryKeys.PM_SPRINT_PRODUCTS,
        detail,
      ]) as ListResponse<ClientProductEntity>;

      // Answered rather than emptied: the same fields the server writes, so the screen reads the way it
      // will after the refetch — the remark still there, as the question the dismissal answered.
      handleDetailMutation(queryClient, detail, {
        id: values.sprintProductId,
        disputeResolution: 'dismissed',
        disputeResolutionNote: values.note?.trim() || undefined,
        disputeResolvedAt: new Date(),
        disputeResolvedBy: { email: tokenData?.email ?? '' },
        viewerHasUnreadComment: false,
      });

      return { previousDetail };
    },
    onSuccess: () => {
      toast.success(i18n.t('disputeDismissed'));
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENT_SPRINT_PRODUCTS] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_SPRINT_PRODUCTS_LIST] });
      invalidatePmProductLists(queryClient);
      queryClient.invalidateQueries({ queryKey: pmSprintProductsQueryKey() });
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_UNREAD_COMMENTS] });
    },
    onError: (_, values, context) => {
      const detail = { sprintId: values.sprintId, productId: values.productId };
      queryClient.setQueryData([queryKeys.PM_SPRINT_PRODUCTS, detail], context?.previousDetail);
    },
  });
};

export const usePreviousRecipients = (
  sprintProductId?: string,
): UseQueryResult<ListResponse<MembershipEntity>, IError> =>
  useQuery({
    queryKey: [queryKeys.SPRINT_PRODUCT_PREVIOUS_RECIPIENTS, sprintProductId],
    queryFn: () => api.fetchPreviousRecipients(sprintProductId!),
    enabled: !!sprintProductId,
  });

/** The report mail as it would go out now. Read fresh on each opening: the summary is edited elsewhere. */
export const useSprintReportMailDraft = (
  sprintProductId: string | undefined,
  /** Whose copy to render, for the greeting: the first recipient stands for all of them. */
  firstName: string | undefined,
  totalCost: number | undefined,
  enabled: boolean,
  /** The body as edited, read when a new figure is asked for, so it is repriced rather than redrafted. */
  edited: () => MailBlock[] | null = () => null,
): UseQueryResult<SprintReportMailDraft, IError> =>
  useQuery({
    // Neither the name nor the edits are in the key: keying on them re-renders the mail on every change.
    queryKey: [queryKeys.SPRINT_PRODUCT_MAIL_DRAFT, sprintProductId, totalCost],
    queryFn: () =>
      api.fetchSprintReportMailDraft(sprintProductId!, {
        firstName: firstName || undefined,
        totalCost,
        blocks: edited() ?? undefined,
      }),
    enabled: enabled && !!sprintProductId,
    gcTime: 0,
  });

export const useSendSprintProduct = (): UseMutationResult<
  SprintProductEntity,
  IError,
  SendSprintFormValues,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: SendSprintFormValues) => api.sendSprintProduct(values),
    onSuccess: sprintProduct => {
      toast.success(i18n.t('reportSent'));
      queryClient.invalidateQueries({ queryKey: [queryKeys.SPRINT_PRODUCT_CLIENTS, sprintProduct.id] });
      invalidatePmProductLists(queryClient);
      // Sending closes the sprint, which is what puts it in the "awaiting approval" list.
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_SPRINT_PRODUCTS_LIST] });
    },
  });
};

export const useClientSprintProducts = (
  organizationId?: string,
  params?: {
    statuses?: Partial<SprintProductStatus>[];
  },
): UseQueryResult<{ items: SprintProductClientEntity[]; count: number }, IError> =>
  useQuery({
    queryKey: [queryKeys.CLIENT_SPRINT_PRODUCTS, params],
    queryFn: () => api.fetchClientSprintProducts(organizationId, params),
    enabled: !!organizationId,
    staleTime: 1000,
  });

/** The client's list, whoever is reading: in the preview, scoped to the product rather than the reader. */
export const useSprintProductsForViewer = (
  internal: boolean,
  organizationId?: string,
  params?: {
    statuses?: Partial<SprintProductStatus>[];
    productId?: string;
  },
): UseQueryResult<{ items: SprintProductClientEntity[]; count: number }, IError> =>
  useQuery({
    queryKey: [queryKeys.CLIENT_SPRINT_PRODUCTS, { organizationId, params, internal }],
    queryFn: () => api.fetchClientSprintProducts(organizationId, internal ? { ...params, asClient: true } : params),
    // Nothing to ask for until the product is known, since that is what stands in for the client.
    enabled: !!organizationId && (!internal || !!params?.productId),
    staleTime: 1000,
  });

/** Closed, sent, and with time in them: a report nobody sent is waiting on Panenco, and an empty one on nobody. */
export const useListPmSprintProducts = (
  organizationId: string | undefined,
  params?: {
    statuses?: SprintProductStatus[];
    sent?: boolean;
    hasReportedTime?: boolean;
    wholeHistory?: boolean;
    productId?: string;
  },
): UseQueryResult<ListResponse<SprintProductEntity>, IError> =>
  useQuery({
    queryKey: [queryKeys.PM_SPRINT_PRODUCTS_LIST, { organizationId, params }],
    queryFn: () => api.fetchPmSprintProducts(organizationId, params),
    enabled: !!organizationId,
  });

// Ended sprints a PM still owes a close, for the Insights card. Paged: an org that has never closed
// anything has a row per (sprint, product) since it started, and the card only shows the top of that.
// Staging's signature, not the prototype's `{ items; count }`: it is the same shape, and the Insights
// card that reads it is staging's.
export const usePmUnclosedSprints = (
  organizationId: string | undefined,
  params?: { offset?: number; limit?: number },
): UseQueryResult<ListResponse<UnclosedSprintEntity>, IError> =>
  useQuery({
    queryKey: [queryKeys.PM_UNCLOSED_SPRINTS, { organizationId, params }],
    queryFn: () => api.fetchPmUnclosedSprints(organizationId, params),
    enabled: !!organizationId,
    staleTime: 1000,
  });

/** Who an approval or a remark goes to by default, in the two groups the dialog files them under. */
export const useApprovalRecipients = (sprintProductId?: string): UseQueryResult<ApprovalRecipients, IError> =>
  useQuery({
    queryKey: [queryKeys.APPROVAL_RECIPIENTS, sprintProductId],
    queryFn: () => api.fetchApprovalRecipients(sprintProductId),
    enabled: !!sprintProductId,
  });

export const useListSprintProductClients = (
  sprintProductId?: string,
  options?: Omit<UseQueryOptions<ListResponse<MembershipEntity>, IError>, 'queryKey' | 'queryFn'>,
): UseQueryResult<ListResponse<MembershipEntity>, IError> =>
  useQuery({
    queryKey: [queryKeys.SPRINT_PRODUCT_CLIENTS, sprintProductId],
    queryFn: () => api.fetchSprintProductClients(sprintProductId),
    staleTime: 1000,
    enabled: !!sprintProductId,
    ...options,
  });

export const useRemoveSprintProductClient = (
  sprintProductId?: string,
): UseMutationResult<void, IError, MembershipEntity, void> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: client => api.removeSprintProductClient(sprintProductId, client.id),
    onMutate: async client => {
      queryClient.setQueryData(
        [queryKeys.SPRINT_PRODUCT_CLIENTS, sprintProductId],
        (old: ListResponse<MembershipEntity>) => {
          const newItems = old.items.filter(x => x.user.email !== client.user.email);
          return { count: old.count - 1, items: newItems };
        },
      );
    },
  });
};

export const useResendSprintProduct = (): UseMutationResult<void, IError, { sprintId: string; productId: string }> =>
  useMutation({
    mutationFn: ({ sprintId, productId }) => api.resendSprintProduct(sprintId, productId),
  });

/**
 * Records that our side has read a client's approval comment, clearing the report's red dot. Fired when
 * the approval is opened, so reading it is what marks it read — there is no separate "mark as read".
 */
export const useMarkApprovalCommentRead = (): UseMutationResult<SprintProductEntity, IError, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sprintProductId: string) => api.markApprovalCommentRead(sprintProductId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pmSprintProductsQueryKey() });
      // The dot also rides on the PM product lists, which count unread comments per product.
      invalidatePmProductLists(queryClient);
      // And on the Products nav item, which counts the same comments. Left out, the dot cleared here
      // while the badge kept counting a comment that had just been read.
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_UNREAD_COMMENTS] });
    },
  });
};

/** Everywhere a dot or a count is drawn from this reader's queue. */
const refreshUnreadSurfaces = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries({ queryKey: [queryKeys.PM_UNREAD_COMMENTS] });
  queryClient.invalidateQueries({ queryKey: pmSprintProductsQueryKey() });
  invalidatePmProductLists(queryClient);
};

const useUndoMarkAllCommentsRead = (organizationId?: string): UseMutationResult<void, IError, string[]> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sprintProductIds: string[]) => api.undoMarkAllCommentsRead(organizationId, sprintProductIds),
    onSuccess: () => refreshUnreadSurfaces(queryClient),
  });
};

/**
 * Clearing the whole queue at once, and putting it back.
 *
 * The undo is the reason this hands the toast what it marked rather than a count: by the time someone
 * presses it a new remark may have arrived, and dragging that one back to unread would undo something
 * the reader never asked to clear.
 */
export const useMarkAllCommentsRead = (
  organizationId?: string,
): UseMutationResult<{ sprintProductIds: string[] }, IError, void> => {
  const queryClient = useQueryClient();
  const { mutate: undo } = useUndoMarkAllCommentsRead(organizationId);

  return useMutation({
    mutationFn: () => api.markAllCommentsRead(organizationId),
    onSuccess: ({ sprintProductIds }) => {
      refreshUnreadSurfaces(queryClient);
      if (!sprintProductIds.length) return;

      toast.success(i18n.t('commentsMarkedRead', { count: sprintProductIds.length }), {
        action: { label: i18n.t('actions.undo'), onClick: () => undo(sprintProductIds) },
      });
    },
  });
};

/** Comments waiting on the caller to read them — the count on the Products nav item. */
export const useUnreadCommentCount = (
  organizationId?: string,
): UseQueryResult<{ count: number; sprintIds: string[] }, IError> =>
  useQuery({
    queryKey: [queryKeys.PM_UNREAD_COMMENTS, organizationId],
    queryFn: () => api.fetchUnreadCommentCount(organizationId),
    enabled: !!organizationId,
  });
